import { forwardRef, useEffect } from 'react';

import { getTextInputStyles, type OnInputEvent } from './helpers';
import { InputController, type InputProps } from './InputController';

import { useForwardedRef } from '../../hooks/useForwardedRef';
import { classNames } from '../../server/utils/classNames';

export type MultilineTextInputFieldProps = Omit<InputProps<HTMLTextAreaElement>, 'type'> & {
	fieldSizing?: 'content' | 'fixed';
	type?: Exclude<InputProps<HTMLTextAreaElement>['type'], 'search'>;
};

export const MultilineTextInputField = forwardRef<HTMLTextAreaElement, MultilineTextInputFieldProps>(
	({ className, endAdornment, autoFocus, hint, invalid, error, label, fieldSizing, ...rest }, ref) => {
		const forwardedRef = useForwardedRef(ref);
		const textAreaHeight = '72';

		// By default a textarea element places cursor at the beginning of the text while it should be at the end
		useEffect(() => {
			const textarea = forwardedRef.current;
			if (textarea) {
				if (autoFocus) {
					textarea.focus();
					textarea.selectionStart = textarea.value.length;
					textarea.selectionEnd = textarea.value.length;
				}
			}
		}, [autoFocus, forwardedRef]);

		const inputClasses = classNames(getTextInputStyles(!!invalid, rest.disabled), className);

		const autoAdjustHeight = (element: HTMLTextAreaElement) => {
			element.style.height = `${textAreaHeight}px`; // reset to initial height to account for decrease in height
			element.style.height = `${element.scrollHeight}px`;
		};

		return (
			<InputController
				endAdornment={endAdornment}
				forwardedRef={forwardedRef}
				containerClassName="py-4"
				getEyelidClassName={shouldRaiseEyelid =>
					classNames({
						'top-0 pt-4': shouldRaiseEyelid,
						'top-[22px]': !shouldRaiseEyelid,
					})
				}
				hint={hint}
				invalid={invalid}
				error={error}
				label={label}
				{...rest}
				render={({ handleBlur, handleChange, handleFocus, handleOnInputChange, ...inputProps }) => {
					const onInputHandler = (e: OnInputEvent<HTMLTextAreaElement>) => {
						if (fieldSizing !== 'fixed') autoAdjustHeight(e.currentTarget);
						handleOnInputChange?.(e);
					};
					return (
						<textarea
							rows={3}
							{...inputProps}
							className={inputClasses}
							onBlur={handleBlur}
							onChange={handleChange}
							onFocus={handleFocus}
							onInput={onInputHandler}
							ref={forwardedRef}
						/>
					);
				}}
			/>
		);
	},
);
